import { Scale, ShoppingCart, Store, AlertTriangle } from "lucide-react";
import { Badge, Card, EmptyState, Notice, SectionHeading } from "./ui";
import type { QuoteDto } from "../lib/api";
import { formatINR } from "../lib/format";

function QuoteColumn({ quote, bestTotal }: { quote: QuoteDto; bestTotal: number | null }) {
  const b = quote.breakdown;
  const isBest = bestTotal !== null && b.finalTotalPaise === bestTotal;
  return (
    <Card className={`p-6 ${isBest ? "border-emerald-400/40 ring-2 ring-emerald-400/40" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {quote.channel === "online" ? (
              <ShoppingCart className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
            ) : (
              <Store className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
            )}
            <h4 className="text-lg font-semibold text-white">{quote.title}</h4>
          </div>
          <p className="mt-1 text-sm text-wandor-muted">
            {quote.vendorName || "Vendor not named"} · {quote.channel} quotation
          </p>
        </div>
        {isBest && <Badge tone="success">Lower total</Badge>}
      </div>

      <dl className="mt-5 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-wandor-muted">Base price</dt>
          <dd className="tabular-nums text-wandor-text">{formatINR(b.basePricePaise)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-wandor-muted">
            GST {b.gstBps !== null ? `(${b.gstBps / 100}%)` : ""} —{" "}
            {b.taxInclusive ? "included in base" : "added to base"}
          </dt>
          <dd className="tabular-nums text-wandor-text">
            {b.gstSource === "none" ? "Not quoted" : formatINR(b.gstPaise)}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-wandor-muted">Payment/convenience fee</dt>
          <dd className="tabular-nums text-wandor-text">{formatINR(b.convenienceFeePaise)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-wandor-muted">Other quoted charges</dt>
          <dd className="tabular-nums text-wandor-text">{formatINR(b.otherChargesPaise)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-wandor-muted">Discount</dt>
          <dd className="tabular-nums text-wandor-text">−{formatINR(b.discountPaise)}</dd>
        </div>
        {b.quotedFinalTotalPaise !== null && (
          <div className="flex justify-between gap-3">
            <dt className="text-wandor-muted">Quoted final payable</dt>
            <dd className="tabular-nums text-wandor-text">{formatINR(b.quotedFinalTotalPaise)}</dd>
          </div>
        )}
        <div className="flex justify-between gap-3 border-t border-white/10 pt-2">
          <dt className="font-semibold text-white">Final payable total</dt>
          <dd className="tabular-nums text-lg font-semibold text-white">
            {formatINR(b.finalTotalPaise)}
          </dd>
        </div>
      </dl>

      {quote.notes && <p className="mt-3 text-xs text-wandor-muted">{quote.notes}</p>}
      {quote.quoteRef && <p className="mt-1 text-xs text-white/40">Quote ref: {quote.quoteRef}</p>}
      {b.gstSource === "none" && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          No GST amount or rate was stated on this quotation. Zero GST is shown because the quote says
          so — the committee must verify the vendor&rsquo;s invoice and applicable tax treatment.
        </p>
      )}
    </Card>
  );
}

export default function QuoteComparison({ quotes }: { quotes: QuoteDto[] }) {
  const pairs: Array<{ label: string; list: QuoteDto[] }> = [];
  const byKey = new Map<string, QuoteDto[]>();
  for (const q of quotes) {
    const key = `${q.category}`;
    const arr = byKey.get(key) ?? [];
    arr.push(q);
    byKey.set(key, arr);
  }
  for (const [category, list] of byKey) {
    pairs.push({ label: `${category} quotations`, list });
  }

  if (quotes.length === 0) {
    return (
      <div className="mt-12">
        <SectionHeading
          eyebrow="Vendor quote comparison"
          title="Online vs offline quotations"
          description="Side-by-side vendor quotes will appear here once the committee records them."
        />
        <div className="mt-6">
          <EmptyState title="No vendor quotations recorded yet" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12">
      <SectionHeading
        eyebrow="Vendor quote comparison"
        title="Online vs offline quotations"
        description="Each quote is broken into base price, GST as stated on the quotation, payment or convenience fees, discounts, and other quoted charges. GST is never inferred from the payment channel — an offline quote shows zero GST only when the quotation itself supports it."
      />

      <div className="mt-8 space-y-10">
        {pairs.map((pair) => {
          const totals = pair.list.map((q) => q.breakdown.finalTotalPaise);
          const bestTotal = totals.length > 1 ? Math.min(...totals) : null;
          const online = pair.list.find((q) => q.channel === "online");
          const offline = pair.list.find((q) => q.channel === "offline");
          const diff =
            online && offline
              ? online.breakdown.finalTotalPaise - offline.breakdown.finalTotalPaise
              : null;
          return (
            <div key={pair.label}>
              <h3 className="flex items-center gap-2 text-xl font-semibold text-white">
                <Scale className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
                {pair.label.charAt(0).toUpperCase() + pair.label.slice(1)}
              </h3>
              <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                {pair.list.map((q) => (
                  <QuoteColumn key={q.id} quote={q} bestTotal={bestTotal} />
                ))}
              </div>
              {diff !== null && (
                <div className="mt-4">
                  <Notice tone="info">
                    Difference:{" "}
                    <strong>
                      {diff === 0
                        ? "both quotations total the same"
                        : `${formatINR(Math.abs(diff))} ${
                            diff > 0 ? "more for the online quotation" : "more for the offline quotation"
                          }`}
                    </strong>
                    . Compare components above before deciding — taxes and fees must be verified against
                    the vendor&rsquo;s invoice.
                  </Notice>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
